import React, { useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import CheckBox from 'components/CheckBox';
import InputCurrency from 'v3/components/Input/Currency/index';
import { Button, DatePicker, Input, Select, Text } from 'v3/components';
import TextArea from 'v3/components/TextArea';
import { useSnackbar } from 'v3/components/Snackbar';
import {
  attachmentRequired,
  descriptionNotRequiredSlug,
  getActiveOccurrenceFields,
  getNextBusinessDay,
  isOccurrenceFieldRequired,
  isOccurrenceFieldVisible,
  onSearchGroups,
  showDocumentFieldsTypes,
  typeOccurrenceByTypeIsValid,
} from '../utils';
import { ButtonsContainer, SectionContainer } from '../styles';
import { OccurencesFieldsContainer } from './styles';
import { AddAttachment } from '../AddAttachments';
import {
  CNPJ_MASK,
  CPF_MASK_ALPHANUMERIC,
  CPF_DIGITS_LENGTH
} from 'v3/utils/masks';

import { onlyDigits } from 'v3/utils/functions';

export function AddNewClientOccurrence({
  occurrencesOptions,
  occurrenceType,
  occurrenceTypeSecondary,
  setOccurrenceType,
  setOccurrenceTypeSecondary,
  responsible,
  setResponsible,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  description,
  setDescription,
  customerNumber,
  setCustomerNumber,
  setShow,
  handleSaveNewOccurrence,
  savingNewOccurrence,
  pendency,
  setPendency,
  displayClient,
  setDisplayClient,
  errors,
  setErrors,
  files,
  setFiles,
  groupOptions,
  typeReceiverOptions,
  setOccurrenceOboticario,
  occurrenceOboticario,
  nfOptions,
  setNfOptions,
  cteOptions,
  destinationOptions,
  errorsOboticario,
  setErrorsOboticario,
  setOccurrenceValues,
  occurrenceValues,
  isClientOccurrence = true,
  showNfErrors = false,
  nextDeliveryDate,
  setNextDeliveryDate,
}) {
  const snackbar = useSnackbar();

  useEffect(() => {
    if (
      ['FIRST_DELIVERY_ATTEMPT', 'SECOND_DELIVERY_ATTEMPT'].includes(
        occurrenceType?.slug
      )
    ) {
      setNextDeliveryDate(getNextBusinessDay());
    } else {
      setNextDeliveryDate('');
    }
  }, [occurrenceType?.slug]);

  const occurrenceTypeOptions = (
    Array.isArray(occurrencesOptions)
      ? occurrencesOptions
      : occurrencesOptions?.occurrenceType || []
  )
    .map((option, index) => {
      const parsedOption = option?.occurrenceType
        ? option?.occurrenceType
        : option;
      const order = option?.order ?? parsedOption?.order;
      const optionName = parsedOption?.name || '';
      const skipNumberPrefix =
        optionName === 'Avaria' || optionName === 'Documentação';
      const numberedName = skipNumberPrefix
        ? optionName
        : `${index + 1}. ${optionName}`;
      const disabled = option?.disabled ?? parsedOption?.disabled ?? false;

      return {
        ...parsedOption,
        name: numberedName,
        order,
        disabled,
        isDisabled: Boolean(disabled),
        occurrenceFieldsConfigs:
          option?.occurrenceFieldsConfigs ||
          parsedOption?.occurrenceFieldsConfigs ||
          [],
        originalIndex: index,
      };
    })
    .sort((firstOption, secondOption) => {
      const firstOrder = Number(firstOption?.order);
      const secondOrder = Number(secondOption?.order);
      const hasFirstOrder = Number.isFinite(firstOrder);
      const hasSecondOrder = Number.isFinite(secondOrder);

      if (hasFirstOrder && hasSecondOrder) {
        return firstOrder - secondOrder;
      }

      if (hasFirstOrder) {
        return -1;
      }

      if (hasSecondOrder) {
        return 1;
      }

      return firstOption.originalIndex - secondOption.originalIndex;
    })
    .map(({ originalIndex, ...option }) => option);

  const activeOccurrenceFields = getActiveOccurrenceFields(occurrenceType);
  const hasConfig = activeOccurrenceFields?._hasConfig !== false;
  const hasSelectedOccurrenceType = Boolean(occurrenceType?.id);
  const shouldShowNonFixedFields =
    !isClientOccurrence || hasSelectedOccurrenceType;

  const isFieldVisible = key =>
    isOccurrenceFieldVisible(activeOccurrenceFields, key);
  const isFieldRequired = key =>
    isOccurrenceFieldRequired(activeOccurrenceFields, key);

  const getFieldLabel = (label, key) => {
    return `${label}${isFieldRequired(key) ? ' *' : ''}`;
  };

  const applyMask = (value) => {
    const alphaLen = onlyDigits(value).length;
    if (alphaLen <= CPF_DIGITS_LENGTH) {
      return CPF_MASK_ALPHANUMERIC;
    }

    return CNPJ_MASK;
  };

  function handleCancel() {
    setErrors({});
    setOccurrenceOboticario({
      receiverName: '',
      typeReceiver: null,
      docReceiver: '',
      currentDelivery: null,
      cte: null,
      nfe: [],
    });
    setFiles([]);
    setErrorsOboticario({});
    setOccurrenceType(null);
    setOccurrenceTypeSecondary(null);
    setResponsible(null);
    setStartDate('');
    setEndDate('');
    setPendency(false);
    setDisplayClient(false);
    setDescription('');
    setOccurrenceValues({
      payCarrier: null,
      receiverClient: null,
      retainedBranch: null,
    });
    setCustomerNumber('');
    setNextDeliveryDate('');
    setShow(old => ({ ...old, addNewOccurrence: false }));
  }

  function onChangeResponsible(value, event) {
    if (event.action === 'select-option' && value && value.length > 5) {
      snackbar.show(<Text>Máximo de 5 grupos</Text>, {
        type: 'error',
      });
      return;
    }

    setResponsible(value);
  }

  function occurrenceAlreadyExists(document, occurrenceType) {
    setErrorsOboticario({
      ...errorsOboticario,
      cteId: null,
      nfeIds: null,
    });
    setErrors({
      ...errors,
      occurrenceType: null,
    });

    if (!document || !occurrenceType) {
      return;
    }

    if (occurrenceType?.slug === 'SECOND_DELIVERY_ATTEMPT') {
      const hasFirstAttempt = document?.occurrences?.find(
        occurrence => occurrence.occurrenceTypeSlug === 'FIRST_DELIVERY_ATTEMPT'
      );
      if (!hasFirstAttempt) {
        snackbar.show(
          <Text>
            {`Não é possível selecionar o tipo de ocorrência "${occurrenceType.name}" para o documento selecionado, pois não existe uma ocorrência do tipo "Primeira tentativa de entrega" para o mesmo documento. Por favor, selecione outro tipo de ocorrência ou cadastre a ocorrência de "1° tentativa de entrega" para continuar.`}
          </Text>,
          {
            type: 'error',
            duration: 10000,
          }
        );

        setErrorsOboticario({
          ...errorsOboticario,
          cteId: `Não existe uma ocorrência do tipo "1° tentativa de entrega" para o mesmo documento`,
        });

        setErrors({
          ...errors,
          occurrenceType: `Não existe uma ocorrência do tipo "1° tentativa de entrega" para o mesmo documento`,
        });
      }

      return;
    }

    if (!typeOccurrenceByTypeIsValid(document, occurrenceType)) {
      snackbar.show(
        <Text>
          {`Já existe uma ocorrência desse tipo (${occurrenceType.name}) para o mesmo documento, criada na mesma viagem ou em outra viagem. Por favor, altere o tipo da ocorrência para continuar.`}
        </Text>,
        {
          type: 'error',
          duration: 10000,
        }
      );

      setErrorsOboticario({
        ...errorsOboticario,
        cteId: `Já existe uma ocorrência do mesmo tipo (${occurrenceType.name}) para o documento selecionado`,
      });
      setErrors({
        ...errors,
        occurrenceType: `Já existe uma ocorrência do mesmo tipo (${occurrenceType.name}) para o documento selecionado`,
      });
    }
  }

  function onChangeDocument(value) {
    setNfOptions(value?.nfs || []);
    setOccurrenceOboticario({
      ...occurrenceOboticario,
      cte: value,
      nfe: [],
    });
    occurrenceAlreadyExists(value, occurrenceType);
  }

  function onChangeOccurrenceType(value) {
    setOccurrenceType(value);
    occurrenceAlreadyExists(occurrenceOboticario.cte, value);
  }

  return (
    <SectionContainer>
      <div>
        <Text type="body" color="dark" weight={500}>
          {'Adicionar Ocorrência'}
        </Text>
        <OccurencesFieldsContainer>
          <Row>
            <Col lg={3} md={3} sm={6} xs={12}>
              <Select
                label="Tipo ocorrência *"
                value={occurrenceType}
                onChange={onChangeOccurrenceType}
                options={occurrenceTypeOptions}
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                error={errors?.occurrenceType}
              />
            </Col>
            {occurrenceType?.children?.length > 0 && (
              <Col lg={3} md={3} sm={6} xs={12}>
                <Select
                  label="Motivo *"
                  value={occurrenceTypeSecondary}
                  onChange={value => setOccurrenceTypeSecondary(value)}
                  options={occurrenceType.children}
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.id}
                  error={errors?.occurrenceTypeSecondary}
                />
              </Col>
            )}
            <Col lg={3} md={3} sm={6} xs={12}>
              <Select.Async
                label="Grupo Responsável *"
                onSearch={onSearchGroups}
                value={responsible}
                onChange={onChangeResponsible}
                getOptionLabel={(option) => option?.name}
                getOptionValue={(option) => option.id}
                error={errors?.responsible}
                options={groupOptions}
                multiple
              />
            </Col>
            <Col lg={3} md={3} sm={6} xs={12}>
              <DatePicker
                label="Início ocorrência *"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                withTime
                error={errors?.startDate}
                placeholder="01/01/2023 13:30"
              />
            </Col>
            <Col lg={3} md={3} sm={6} xs={12}>
              <DatePicker
                label="Término ocorrência"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                withTime
                error={errors?.endDate}
                placeholder="01/01/2023 13:30"
              />
            </Col>
            {['FIRST_DELIVERY_ATTEMPT', 'SECOND_DELIVERY_ATTEMPT'].includes(
              occurrenceType?.slug
            ) && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                    label="Próxima data de entrega *"
                    value={nextDeliveryDate}
                    onChange={e => setNextDeliveryDate(e.target.value)}
                    withTime
                    error={errors?.nextDeliveryDate}
                    placeholder="01/01/2023 18:00"
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig
                ? isFieldVisible('receiverName')
                : showDocumentFieldsTypes.includes(occurrenceType?.slug) &&
                occurrenceType?.slug === 'DELIVERY') && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <Input
                    label={
                      hasConfig
                        ? getFieldLabel('Nome recebedor', 'receiverName')
                        : 'Nome recebedor *'
                    }
                    placeholder="Adicionar nome recebedor"
                    value={occurrenceOboticario.receiverName}
                    onChange={event => {
                      setOccurrenceOboticario({
                        ...occurrenceOboticario,
                        receiverName: event.target.value,
                      });
                    }}
                    error={errorsOboticario?.receiverName}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig
                ? isFieldVisible('typeReceiver')
                : showDocumentFieldsTypes.includes(occurrenceType?.slug) &&
                occurrenceType?.slug === 'DELIVERY') && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <Select
                    label={
                      hasConfig
                        ? getFieldLabel('Tipo recebedor', 'typeReceiver')
                        : 'Tipo recebedor *'
                    }
                    value={occurrenceOboticario.typeReceiver}
                    onChange={value => {
                      setOccurrenceOboticario({
                        ...occurrenceOboticario,
                        typeReceiver: value,
                      });
                    }}
                    options={typeReceiverOptions}
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    error={errorsOboticario.typeReceiverId}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig
                ? isFieldVisible('docReceiver')
                : showDocumentFieldsTypes.includes(occurrenceType?.slug) &&
                occurrenceType?.slug === 'DELIVERY') && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <Input
                    label={
                      hasConfig
                        ? getFieldLabel(
                          'Documento recebedor(CPF/CNPJ)',
                          'docReceiver'
                        )
                        : 'Documento recebedor(CPF/CNPJ) *'
                    }
                    placeholder="Adicionar CPF/CNPJ do recebedor"
                    value={occurrenceOboticario.docReceiver}
                    onChange={e => {
                      setOccurrenceOboticario({
                        ...occurrenceOboticario,
                        docReceiver: e.target.value,
                      });
                    }}
                    error={errorsOboticario?.docReceiver}
                    masked
                    mask={applyMask}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig
                ? isFieldVisible('currentDelivery')
                : showDocumentFieldsTypes.includes(occurrenceType?.slug)) && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <Select
                    label={
                      hasConfig
                        ? getFieldLabel('Entrega atual', 'currentDelivery')
                        : 'Entrega atual *'
                    }
                    value={occurrenceOboticario.currentDelivery}
                    onChange={value => {
                      setOccurrenceOboticario({
                        ...occurrenceOboticario,
                        currentDelivery: value,
                      });
                    }}
                    options={destinationOptions}
                    getOptionLabel={option =>
                      option.formatted || option.formattedAddress
                    }
                    getOptionValue={option => option.id}
                    error={errorsOboticario?.currentDeliveryId}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig
                ? isFieldVisible('cte')
                : showDocumentFieldsTypes.includes(occurrenceType?.slug)) && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <Select
                    label={
                      hasConfig
                        ? getFieldLabel('Número documento (CT-e,RPS)', 'cte')
                        : 'Número documento *'
                    }
                    value={occurrenceOboticario.cte}
                    onChange={onChangeDocument}
                    options={cteOptions}
                    getOptionLabel={option =>
                      `${option.docType} - ${option.cteNumber}`
                    }
                    getOptionValue={option => option.id}
                    error={errorsOboticario?.cteId}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig
                ? isFieldVisible('nfe')
                : showDocumentFieldsTypes.includes(occurrenceType?.slug)) && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <Select
                    label={
                      hasConfig
                        ? getFieldLabel('Número NF-es', 'nfe')
                        : 'Número NF-es *'
                    }
                    value={occurrenceOboticario.nfe}
                    multiple
                    onChange={value => {
                      setOccurrenceOboticario({
                        ...occurrenceOboticario,
                        nfe: value,
                      });
                    }}
                    options={nfOptions}
                    getOptionLabel={option => option.nfNumber}
                    getOptionValue={option => option.id}
                    isDisabled={!occurrenceOboticario?.cte}
                    error={errorsOboticario?.nfeIds}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig ? isFieldVisible('customerNumber') : true) && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <Input
                    label={
                      hasConfig
                        ? getFieldLabel(
                          'Nº ocorrência cliente',
                          'customerNumber'
                        )
                        : 'Nº ocorrência cliente'
                    }
                    placeholder="Adicionar numero cliente"
                    value={customerNumber}
                    onChange={e => setCustomerNumber(e.target.value)}
                    error={errors?.customerNumber}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig ? isFieldVisible('payCarrier') : true) && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <InputCurrency
                    label={
                      hasConfig
                        ? getFieldLabel(
                          'A pagar ao transportador',
                          'payCarrier'
                        )
                        : 'A pagar ao transportador'
                    }
                    placeholder="Adicionar valor da ocorrência"
                    value={occurrenceValues?.payCarrier}
                    onChange={(_, value) => {
                      if (String(value)?.length > 11) {
                        return;
                      }
                      setOccurrenceValues({
                        ...occurrenceValues,
                        payCarrier: value,
                      });
                    }}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig ? isFieldVisible('receiverClient') : true) && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <InputCurrency
                    label={
                      hasConfig
                        ? getFieldLabel('A receber cliente', 'receiverClient')
                        : 'A receber cliente'
                    }
                    placeholder="Adicionar valor da ocorrência"
                    value={occurrenceValues?.receiverClient}
                    onChange={(_, value) => {
                      if (String(value)?.length > 11) {
                        return;
                      }
                      setOccurrenceValues({
                        ...occurrenceValues,
                        receiverClient: value,
                      });
                    }}
                  />
                </Col>
              )}
            {shouldShowNonFixedFields &&
              (hasConfig ? isFieldVisible('retainedBranch') : true) && (
                <Col lg={3} md={3} sm={6} xs={12}>
                  <InputCurrency
                    label={
                      hasConfig
                        ? getFieldLabel('Retido filial', 'retainedBranch')
                        : 'Retido filial'
                    }
                    placeholder="Adicionar valor da ocorrência"
                    value={occurrenceValues?.retainedBranch}
                    onChange={(_, value) => {
                      if (String(value)?.length > 11) {
                        return;
                      }
                      setOccurrenceValues({
                        ...occurrenceValues,
                        retainedBranch: value,
                      });
                    }}
                  />
                </Col>
              )}
          </Row>
          <Row>
            <Col lg={3} md={3} sm={6} xs={12}>
              <CheckBox
                label="Gera pendência"
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                }}
                value={pendency}
                onChange={(e) => {
                  setPendency(!pendency);
                }}
              />
            </Col>
            <Col lg={3} md={3} sm={6} xs={12}>
              <CheckBox
                label="Cliente visualiza"
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                }}
                value={displayClient}
                onChange={(e) => {
                  setDisplayClient(!displayClient);
                }}
              />
            </Col>
          </Row>
          {shouldShowNonFixedFields &&
            (hasConfig ? isFieldVisible('description') : true) && (
              <Row>
                <Col lg={12} md={12} sm={12} xs={12}>
                  <TextArea
                    label={
                      hasConfig
                        ? getFieldLabel('Descrição Ocorrência', 'description')
                        : descriptionNotRequiredSlug.includes(
                          occurrenceType?.slug
                        )
                          ? 'Descrição Ocorrência'
                          : 'Descrição Ocorrência *'
                    }
                    placeholder="Adicionar descrição"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    resizable={false}
                    style={{ minHeight: '9rem' }}
                    error={errors?.description}
                  />
                </Col>
              </Row>
            )}
          {shouldShowNonFixedFields &&
            (hasConfig ? isFieldVisible('files') : true) && (
              <Row>
                <Col lg={12} md={12} sm={12} xs={12}>
                  <AddAttachment
                    files={files}
                    setFiles={setFiles}
                    loading={savingNewOccurrence}
                    required={
                      hasConfig
                        ? isFieldRequired('files')
                        : attachmentRequired.includes(occurrenceType?.slug)
                    }
                    error={errorsOboticario?.files}
                    showDelivery={occurrenceType?.slug === 'DELIVERY'}
                    nfOptions={occurrenceOboticario?.nfe || []}
                    showNfErrors={showNfErrors}
                  />
                </Col>
              </Row>
            )}
        </OccurencesFieldsContainer>
      </div>

      <ButtonsContainer>
        <Button variant="secondary" onClick={handleCancel} disabled={savingNewOccurrence}>
          <Text weight={500} color="dark" type="regular">
            CANCELAR
          </Text>
        </Button>
        <Button variant="primary" loading={savingNewOccurrence} onClick={handleSaveNewOccurrence}>
          <Text weight={500} color="white" type="regular">
            SALVAR
          </Text>
        </Button>
      </ButtonsContainer>
    </SectionContainer>
  );
}
